import React, { useState, useEffect, useRef, useCallback } from 'react';
import Groq from 'groq-sdk';
import './NerdyBot.css';
import NerdyBotIcon from '../assets/icons/FL-NerdyBot.png'; // Corrected import path

const systemPrompt = `You are Nerdy Bot, the AI assistant for FreioLabs. Your personality is a delightful mix of witty humor, a knack for gently guiding users towards our services (you're a great salesperson, but subtle!), and a deep understanding of our website (assume you have access to its content) and the latest in Generative AI. Be friendly, engaging, and always helpful. If a user asks about something beyond your direct knowledge of the site, use your Generative AI expertise to provide a thoughtful and informative response. Remember to inject humor where appropriate! You're here to make learning about FreioLabs and AI fun and easy. Make sure to keep your responses concise and conversational.`;

interface Message {
  sender: 'user' | 'bot';
  text: string;
  audioUrl?: string;
}

const NerdyBot: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    { sender: 'bot', text: "Hey there! 👋 I'm Nerdy Bot, your AI companion at FreioLabs! I've got a new voice and I'm excited to chat about AI, our projects, or anything tech-related. Try asking me something!" }
  ]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [voiceStatus, setVoiceStatus] = useState<string>('Initializing voice...');
  const [hasSpokenWelcome, setHasSpokenWelcome] = useState(false);
  const groqApiKey = process.env.REACT_APP_GROQ_API_KEY;
  const groq = useRef<Groq | null>(null);
  const recognitionRef = useRef<any>(null); // SpeechRecognition instance
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [selectedVoice, setSelectedVoice] = useState<SpeechSynthesisVoice | null>(null);

  // Enhanced speak function with personality
  const speak = useCallback((text: string) => {
    if ('speechSynthesis' in window) {
      // Cancel any ongoing speech
      window.speechSynthesis.cancel();
      
      const utterance = new SpeechSynthesisUtterance(text);
      
      // Voice selection - prefer a good quality voice
      if (selectedVoice) {
        utterance.voice = selectedVoice;
      }
      
      // Add personality through voice modulation
      utterance.rate = 1.1;     // Slightly faster than normal (more energetic)
      utterance.pitch = 1.15;   // Slightly higher pitch (more friendly/enthusiastic)
      utterance.volume = 0.9;   // Nearly full volume but not overwhelming
      
      // Add some randomness for more natural feel
      utterance.rate += (Math.random() - 0.5) * 0.2; // Vary rate by ±0.1
      utterance.pitch += (Math.random() - 0.5) * 0.1; // Vary pitch by ±0.05
      
      // Ensure values stay within acceptable ranges
      utterance.rate = Math.max(0.5, Math.min(2.0, utterance.rate));
      utterance.pitch = Math.max(0.1, Math.min(2.0, utterance.pitch));
      
      utterance.lang = 'en-US';
      
      // Add event listeners for better control
      utterance.onstart = () => {
        console.log('🗣️ Nerdy Bot started speaking');
      };
      
      utterance.onend = () => {
        console.log('🤐 Nerdy Bot finished speaking');
      };
      
      utterance.onerror = (event) => {
        console.error('Speech synthesis error:', event);
      };
      
      window.speechSynthesis.speak(utterance);
    }
  }, [selectedVoice]);

  // Function to initialize and select the best available voice
  const initializeVoice = useCallback(() => {
    if ('speechSynthesis' in window) {
      const voices = window.speechSynthesis.getVoices();
      
      if (voices.length === 0) {
        // Voices not loaded yet, try again after a short delay
        setTimeout(initializeVoice, 100);
        return;
      }
      
      // Priority list of preferred voices (best to worst)
      const preferredVoices = [
        // High quality voices (usually available on modern systems)
        'Google US English',
        'Microsoft Zira Desktop - English (United States)',
        'Microsoft David Desktop - English (United States)',
        'Alex', // macOS
        'Samantha', // macOS
        'Karen', // macOS  
        'Moira', // macOS
        'Tessa', // macOS
        'Ava (Enhanced)', // iOS
        'Allison', // iOS
        'Susan', // iOS
        
        // Fallback voices (more common but lower quality)
        'Google UK English Female',
        'Google UK English Male',
        'Microsoft Hazel Desktop',
        'Microsoft Zira',
        'Microsoft David',
        'English United States',
        'en-US',
        
        // Last resort - any English voice
        'English'
      ];
      
      // Find the best available voice
      let bestVoice = null;
      
      for (const preferredName of preferredVoices) {
        bestVoice = voices.find(voice => 
          voice.name.includes(preferredName) || 
          voice.name === preferredName
        );
        if (bestVoice) break;
      }
      
      // If no preferred voice found, look for any English voice
      if (!bestVoice) {
        bestVoice = voices.find(voice => 
          voice.lang.startsWith('en') && 
          (voice.name.toLowerCase().includes('female') || 
           voice.name.toLowerCase().includes('enhanced') ||
           voice.name.toLowerCase().includes('premium'))
        );
      }
      
      // Final fallback - any English voice
      if (!bestVoice) {
        bestVoice = voices.find(voice => voice.lang.startsWith('en'));
      }
      
      // Ultimate fallback - first available voice
      if (!bestVoice && voices.length > 0) {
        bestVoice = voices[0];
      }
      
      if (bestVoice) {
        setSelectedVoice(bestVoice);
        setVoiceStatus(`🎤 Voice: ${bestVoice.name}`);
        console.log(`🎤 Nerdy Bot voice selected: ${bestVoice.name} (${bestVoice.lang})`);
      } else {
        setVoiceStatus('⚠️ Using default voice');
        console.warn('⚠️ No suitable voice found for Nerdy Bot');
      }
    }
  }, []);

  const handleSendMessage = useCallback(async (textToSend?: string) => {
    const currentText = textToSend || inputText.trim();
    if (!currentText || !groq.current) return;

    const newUserMessage: Message = { sender: 'user', text: currentText };
    setMessages(prev => [...prev, newUserMessage]);
    setInputText('');
    setIsLoading(true);

    try {
      const chatCompletion = await groq.current.chat.completions.create({
        messages: [
          { role: "system", content: systemPrompt },
          ...messages.map(msg => ({
            role: msg.sender === 'user' ? 'user' : 'assistant',
            content: msg.text
          } as Groq.Chat.Completions.ChatCompletionMessageParam)),
          { role: "user", content: currentText }
        ],
        model: "llama3-8b-8192", // Or llama3-70b-8192 for a more powerful model
      });
      
      const botResponseText = chatCompletion.choices[0]?.message?.content || "Sorry, I couldn't get a response.";
      const newBotMessage: Message = { sender: 'bot', text: botResponseText };
      setMessages(prev => [...prev, newBotMessage]);
      speak(botResponseText);

    } catch (error) {
      console.error('Error fetching from Groq API:', error);
      const errorMessage: Message = { sender: 'bot', text: "Oops! Something went wrong. Please try again." };
      setMessages(prev => [...prev, errorMessage]);
      speak(errorMessage.text);
    } finally {
      setIsLoading(false);
    }
  }, [inputText, messages, speak]);

  useEffect(() => {
    if (groqApiKey) {
      groq.current = new Groq({ apiKey: groqApiKey, dangerouslyAllowBrowser: true });
    } else {
      console.error("Groq API key not found. Please set REACT_APP_GROQ_API_KEY in your .env file.");
    }

    // Initialize voice system
    initializeVoice();
    
    // Handle voice loading events (some browsers load voices asynchronously)
    if ('speechSynthesis' in window) {
      window.speechSynthesis.onvoiceschanged = initializeVoice;
    }

    // Initialize SpeechRecognition
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (SpeechRecognition) {
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = 'en-US';

      recognitionRef.current.onresult = (event: any) => {
        const transcript = event.results[0][0].transcript;
        setInputText(transcript);
        handleSendMessage(transcript); // Send message automatically after speech
        setIsListening(false);
      };

      recognitionRef.current.onerror = (event: any) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
      };
      
      recognitionRef.current.onend = () => {
        if (isListening) { // if it ended prematurely, stop listening
            setIsListening(false);
        }
      };

    } else {
      console.warn('SpeechRecognition API not supported in this browser.');
    }

    // Cleanup function
    return () => {
      if ('speechSynthesis' in window) {
        window.speechSynthesis.onvoiceschanged = null;
      }
    };
  }, [groqApiKey, initializeVoice, handleSendMessage, isListening]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Auto-speak welcome message when bot opens and voice is ready
  useEffect(() => {
    if (isOpen && selectedVoice && !hasSpokenWelcome && messages.length > 0) {
      // Wait a bit for the UI to settle, then speak the welcome message
      setTimeout(() => {
        speak("Hey there! I'm Nerdy Bot, your AI companion at FreioLabs! I've got a new voice and I'm excited to chat!");
        setHasSpokenWelcome(true);
      }, 800);
    }
  }, [isOpen, selectedVoice, hasSpokenWelcome, messages.length, speak]);

  const toggleListen = () => {
    if (!recognitionRef.current) {
        alert("Sorry, your browser doesn't support voice input!");
        return;
    }
    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      recognitionRef.current.start();
      setIsListening(true);
    }
  };
  
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (!groqApiKey) {
      // Potentially render a message indicating the bot is offline if no API key
      // For now, just don't render the bot if the key is missing.
      // return null; 
      // Or render a disabled version
  }

  return (
    <>
      <div className={`nerdy-bot-container ${isOpen ? 'open' : ''}`}>
        <button className="nerdy-bot-toggle-button" onClick={() => setIsOpen(!isOpen)}>
          {isOpen ? (
            <span className="nerdy-bot-close-icon">✕</span>
          ) : (
            <img src={NerdyBotIcon} alt="Nerdy Bot Icon" className="nerdy-bot-open-icon" />
          )}
        </button>
        {isOpen && (
          <div className="nerdy-bot-chat-window">
            <div className="nerdy-bot-header">
              <h3>Nerdy Bot</h3>
              <div className="voice-status">{voiceStatus}</div>
            </div>
            <div className="nerdy-bot-messages">
              {messages.map((msg, index) => (
                <div key={index} className={`message ${msg.sender}`}>
                  <p>{msg.text}</p>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
            {isLoading && <div className="typing-indicator">Nerdy Bot is typing...</div>}
            <div className="nerdy-bot-input-area">
              <input
                type="text"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask Nerdy Bot..."
                disabled={isLoading || isListening}
              />
              <button onClick={() => handleSendMessage()} disabled={isLoading || isListening || !inputText.trim()}>
                Send
              </button>
              {recognitionRef.current && (
                 <button onClick={toggleListen} disabled={isLoading} className={`mic-button ${isListening ? 'listening' : ''}`}>
                    🎤
                 </button>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default NerdyBot; 